import { useMemo } from 'react';
import { css } from '@emotion/react';
import { BookableAppointmentTypeOption } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/bookable_appointment_type.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { InfinitePaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import {
  Accordion,
  Button,
  CheckboxField,
  ChecklistField,
  InlineNotification,
  SearchField,
  Text,
  useDebouncedValue,
  useControlledField,
  useFormField,
  useTooltip,
} from '@frontend/design-system';
import { AvailabilityFormRow } from '../../availability-form/atoms/availability-form-row';
import { GetAvailabilityRuleFormFieldProps } from '../../availability-form/types';

type ServicesFormProps = {
  locationId: string | undefined;
  getFieldProps: GetAvailabilityRuleFormFieldProps;
};

const ITEM_HEIGHT_PX = 36; // approximate height per checklist item
const MAX_VISIBLE_ITEMS = 15;
const SERVICES_PAGE_SIZE = 100;

export const ServicesForm = ({ locationId, getFieldProps }: ServicesFormProps) => {
  const { t } = useTranslation('schedule-settings');
  const searchField = useFormField({ type: 'text' });
  const debouncedSearchValue = useDebouncedValue(searchField.value);

  const servicesInfiniteQuery = SchedulerV3.Queries.useListBookableAppointmentTypeOptionsQuery({
    request: {
      locationId: locationId || '',
      name: debouncedSearchValue,
      page: 1,
      limit: SERVICES_PAGE_SIZE,
    },
    options: {
      enabled: !!locationId,
      select: (data) => ({
        ...data,
        pages: data.pages.map((page) => ({ rows: page.bookableAppointmentTypes ?? [] })),
      }),
    },
  });

  const servicesFieldProps = getFieldProps('services');
  const selectedServiceIds = useMemo(() => servicesFieldProps.value || [], [servicesFieldProps.value]);

  const allLoadedServices = useMemo(
    () => (servicesInfiniteQuery.data?.pages ?? []).flatMap((page) => page.rows).filter((item) => !!item.id),
    [servicesInfiniteQuery.data]
  );

  const allLoadedServiceIds = useMemo(() => allLoadedServices.map((item) => item.id ?? ''), [allLoadedServices]);

  const listHeight = Math.min(allLoadedServiceIds.length, MAX_VISIBLE_ITEMS) * ITEM_HEIGHT_PX;

  const isAllSelected =
    allLoadedServiceIds.length > 0 && allLoadedServiceIds.every((id) => selectedServiceIds.includes(id));

  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      servicesFieldProps.onChange?.({ name: 'services', value: [] });
    } else {
      const unselectedIds = allLoadedServiceIds.filter((id) => !selectedServiceIds.includes(id));
      servicesFieldProps.onChange?.({ name: 'services', value: unselectedIds });
    }
  };

  const selectAllCheckbox = useControlledField({
    type: 'checkbox',
    value: isAllSelected,
    onChange: handleSelectAllToggle,
  });

  const patientBooking = getFieldProps('patientBooking').value;
  const showSelfBookableWarning = patientBooking === 'DIRECT' && selectedServiceIds.length === 0;

  const headerSubtitle = useMemo(() => {
    if (!allLoadedServices.length) return undefined;
    if (selectedServiceIds.length === 0) return t('No Services Selected');
    if (selectedServiceIds.length === allLoadedServiceIds.length) return t('All services');
    const names = allLoadedServices
      .filter((s) => selectedServiceIds.includes(s.id ?? ''))
      .map((s) => s.displayName ?? '')
      .filter(Boolean);
    return names.join(', ');
  }, [selectedServiceIds, allLoadedServices, allLoadedServiceIds.length, t]);

  return (
    <Accordion.Item value='services'>
      <Accordion.Header title={t('Services')} subtitle={headerSubtitle} />
      <Accordion.Body css={styles.accordionBody}>
        <div>
          <div css={styles.servicesActions}>
            <SearchField
              name='availability-rule-services-search'
              hasNonFloatingLabel
              {...searchField}
              css={styles.searchField}
            />
            <div css={styles.actionRow}>
              <CheckboxField name='select-all-services' label={t('Select All')} {...selectAllCheckbox} />
              <Button
                variant='tertiary'
                css={styles.clearButton}
                disabled={selectedServiceIds.length === 0}
                onClick={() => servicesFieldProps.onChange?.({ name: 'services', value: [] })}
              >
                {t('Clear')}
              </Button>
            </div>
            <hr css={styles.divider} />
          </div>
          <AvailabilityFormRow>
            <div css={styles.servicesWrapper}>
              <ChecklistField label={''} {...getFieldProps('services')} hasNonFloatingLabel css={styles.checklistField}>
                <InfinitePaginatedList
                  height={servicesInfiniteQuery.error ? 250 : listHeight}
                  infiniteQueryProps={servicesInfiniteQuery}
                  renderListItem={({ listItem }) => {
                    return <BookabableAppointmentOptionItem key={listItem.id} listItem={listItem} />;
                  }}
                  emptyStateConfig={{ type: 'schedule', header: t('No matches found') }}
                  endOfListText={t('No more services')}
                />
              </ChecklistField>
            </div>
          </AvailabilityFormRow>
          {showSelfBookableWarning && (
            <AvailabilityFormRow>
              <InlineNotification status='warn' css={styles.warningNotification}>
                <Text size='medium'>
                  {t('Self-bookable availability requires at least one service to be selected.')}
                </Text>
              </InlineNotification>
            </AvailabilityFormRow>
          )}
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
};

const BookabableAppointmentOptionItem = ({ listItem }: { listItem: BookableAppointmentTypeOption }) => {
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  const displayNameText = document.getElementById(`list-item-${listItem.id}`);
  const isEllipsisActive = displayNameText && displayNameText.scrollWidth > displayNameText.clientWidth;

  return (
    <>
      <Tooltip {...tooltipProps}>{listItem.displayName}</Tooltip>
      <ChecklistField.Option key={listItem.id} name={listItem.id ?? ''} css={styles.checklistOption}>
        <div css={styles.checklistOptionText}>
          <Text
            {...(isEllipsisActive ? triggerProps : {})}
            css={styles.checklistOptionLabel}
            id={`list-item-${listItem.id}`}
          >
            {listItem.displayName}
          </Text>
          <Text css={styles.checklistOptionTime}>{listItem.duration} mins</Text>
        </div>
      </ChecklistField.Option>
    </>
  );
};

const styles = {
  accordionBody: css`
    padding: var(--fabric-space-gap-sm) 0 0 var(--fabric-space-gap-sm);
  `,
  servicesActions: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-sm);
    margin-bottom: var(--fabric-space-gap-sm);
  `,
  actionRow: css`
    display: flex;
    align-items: center;
  `,
  clearButton: css`
    margin-left: auto;
    margin-right: var(--fabric-space-gap-sm);
  `,
  divider: css`
    border: none;
    border-top: 1px solid var(--fabric-color-neutral-20);
    margin-right: var(--fabric-space-gap-sm);
  `,
  servicesWrapper: css`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
    margin-bottom: -24px; /* Remove margin of AvailabilityFormRow */
  `,
  searchField: css`
    margin-right: var(--fabric-space-gap-sm);
  `,
  checklistField: css`
    overflow-y: hidden;
    overflow-x: hidden;
    > label {
      margin: 0;
    }
  `,
  checklistOptionLabel: css`
    max-width: 330px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  checklistOptionTime: css`
    text-align: right;
  `,
  checklistOption: css`
    padding-bottom: var(--fabric-space-gap-sm);
    padding-right: var(--fabric-space-gap-sm);
    margin: 0;
    align-items: center;

    label {
      width: 100%;
      margin-bottom: 0;
      padding: 0;
    }
  `,
  checklistOptionText: css`
    display: flex;
    width: 100%;
    justify-content: space-between;
  `,
  warningNotification: css`
    max-height: none;
    max-width: none;
    width: 100%;
    > div {
      width: 100%;
    }
  `,
};
